import * as React from 'react';
import * as _ from 'underscore';
import * as $ from 'jquery';

import { IGlob } from '../../ts/Glob';
import { ProgressBar } from '../../../../_common/platform/tsx/ProgressBar';
import DirsWorkspacesFlat from '../DirsWorkspacesFlat';
import { WorkspacesSection, FoldersSection } from './NavigationSections';
import * as Common from './Common';
import * as permissions from '../../../../_common/platform/ts/permissions';
import { IWorkspacesNavState } from '../../ts/stores/WorkspacesNavStore';

import { SidebarMenuItem, HorizontalMenuItem } from './WorkspacesNavigationItems';

const INSTANCE_ID = 'workspaces-navigation';

interface IWorkspacesNavigationProperties {
    id: string;
    menuMode: Common.MenuModes;
    glob: IGlob;

    onSelect?(id: number, mode: Common.MenuModes): void;
    onShow?(width: number): void;
}

function getStoreState(glob: IGlob) {
    return { ...glob.WorkspacesNavStore.getState() };
}

export default function WorkspacesNavigation(props: IWorkspacesNavigationProperties) {
    const [store, setStore] = React.useState(getStoreState(props.glob));

    const contentRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const handleOnStoreChange = () => {
            setStore(getStoreState(props.glob));
        };

        props.glob.WorkspacesNavStore.addChangeListener(handleOnStoreChange);

        return () => {
            props.glob.WorkspacesNavStore.removeChangeListener(handleOnStoreChange);
        };
    }, []);

    React.useEffect(() => {
        if (props.onShow != null) {
            const div = contentRef.current;

            props.onShow(div.clientWidth);
        }
    });

    const handleOnWorkspaceSelect = (id: number) => {
        if (typeof props.onSelect === 'function') {
            props.onSelect(id, props.menuMode);
        }
    };

    const getSectionClickHanlder = () => {
        return (value: Common.IMenuBranch) => {
            props.glob.AppActions.workspacesNavSetSection(value, props.menuMode);
        };
    };

    const getMenuItemClickHanlder = (menuType: Common.MenuItemTypes) => {
        if (menuType === 'ws') {
            return (value: Common.IMenuBranch) => {
                handleOnWorkspaceSelect(value.refId);
            };
        } else {
            return getSectionClickHanlder();
        }
    };

    const handleGetSectionContent = (menuItem: Common.IMenuBranch) => {
        const section = menuItem.type;

        if (section === Common.SUBMENU) {
            let result = [];

            _.each(menuItem.items, (menuItem) => {
                if (props.menuMode === Common.MenuModes.horizontal) {
                    result.push(
                        <HorizontalMenuItem
                            key={menuItem.type + '_' + menuItem.id}
                            value={menuItem}
                            isSubItem={true}
                            hideIcons={store.hideMenuItemIcons}
                            expandOnMouseOver={store.expandMenuOnMouseOver}
                            treeMode={store.treeMode}
                            glob={props.glob}
                            onClick={getMenuItemClickHanlder(menuItem.type)}
                            getItems={handleGetSectionContent}
                            onShowActions={handleOnShowActions}
                            onHideActions={handleOnHideActions}
                        />
                    );
                } else {
                    result.push(
                        <SidebarMenuItem
                            key={menuItem.type + '_' + menuItem.id}
                            value={menuItem}
                            hideIcons={store.hideMenuItemIcons}
                            glob={props.glob}
                            onClick={getMenuItemClickHanlder(menuItem.type)}
                            getItems={handleGetSectionContent}
                        />
                    );
                }
            });

            return result;
        } else {
            let folderID: string | number;
            let showFolders: boolean;

            switch (section) {
                case Common.RECENT:
                    folderID = Common.RECENT;
                    break;

                case Common.FAVORITES:
                    folderID = Common.FAVORITES;
                    break;

                case Common.FOLDERWS:
                    folderID = menuItem.refId;
                    break;

                case Common.MY_FOLDER:
                    if (store.treeMode === Common.TreeModes.flat) {
                        return getFolderWithWS(menuItem, store);
                    } else {
                        folderID = menuItem.refId;
                        showFolders = true;
                        break;
                    }

                case Common.DIR_BROWSER:
                    if (store.treeMode === Common.TreeModes.flat) {
                        return getFolderWithWS(menuItem, store);
                    } else {
                        folderID = menuItem.refId;
                        showFolders = true;
                        break;
                    }

                case Common.BROWSE_ALL:
                    return getAll(store);

                default:
                    console.log('Section is not supported: ' + section);
                    return;
            }

            let folders: React.JSX.Element;

            if (showFolders) {
                folders = (
                    <FoldersSection
                        value={menuItem.items}
                        menuMode={props.menuMode}
                        hideIcons={store.hideMenuItemIcons}
                        expandMenuOnMouseOver={store.expandMenuOnMouseOver}
                        treeMode={store.treeMode}
                        currentSection={store.currentSection}
                        getSectionContent={handleGetSectionContent}
                        glob={props.glob}
                        onSelect={getSectionClickHanlder()}
                        onShowActions={handleOnShowActions}
                        onHideActions={handleOnHideActions}
                    />
                );
            }

            return (
                <div>
                    {folders}

                    <WorkspacesSection
                        instanceID={INSTANCE_ID}
                        folderID={folderID}
                        sectionType={section}
                        hideIcons={store.hideMenuItemIcons}
                        menuMode={props.menuMode}
                        glob={props.glob}
                        onSelect={handleOnWorkspaceSelect}
                        onShowActions={handleOnShowActions}
                        onHideActions={handleOnHideActions}
                    />
                </div>
            );
        }
    };

    const getFolderWithWS = (menuItem: Common.IMenuBranch, store: IWorkspacesNavState) => {
        if (store.loading) {
            return <ProgressBar />;
        }

        let refID: number;

        if (props.menuMode === Common.MenuModes.horizontal) {
            const { currentSection } = store;

            if (
                currentSection == null ||
                (currentSection.type !== Common.MY_FOLDER && currentSection.type !== Common.DIR_BROWSER)
            ) {
                return;
            }

            refID = currentSection.refId;
        } else {
            refID = menuItem.refId;
        }

        const allowEditing = permissions.canEdit();

        return (
            <DirsWorkspacesFlat
                instanceID={INSTANCE_ID + '-' + (refID || menuItem.type)}
                rootID={refID}
                rootName={menuItem.name}
                useSearching={false}
                allowEditing={allowEditing}
                hideIcons={store.hideMenuItemIcons}
                menuMode={props.menuMode}
                glob={props.glob}
                onWorkspaceSelect={handleOnWorkspaceSelect}
                onShowActions={handleOnShowActions}
                onHideActions={handleOnHideActions}
            />
        );
    };

    const handleOnShowActions = () => {
        props.glob.AppActions.workspaceSetActionsMenuIsShown(true);
    };

    const handleOnHideActions = () => {
        props.glob.AppActions.workspaceSetActionsMenuIsShown(false);
    };

    const getAll = (store: IWorkspacesNavState) => {
        if (store.loading) {
            return <ProgressBar />;
        }

        return (
            <DirsWorkspacesFlat
                instanceID={INSTANCE_ID}
                hideIcons={store.hideMenuItemIcons}
                menuMode={props.menuMode}
                glob={props.glob}
                onWorkspaceSelect={handleOnWorkspaceSelect}
                onShowActions={handleOnShowActions}
                onHideActions={handleOnHideActions}
            />
        );
    };

    const content = [];

    let className: string;

    if (props.menuMode === Common.MenuModes.horizontal) {
        className = 'horizontal-menu';

        _.each(store.menu, (menuItem) => {
            content.push(
                <HorizontalMenuItem
                    key={menuItem.type + '_' + menuItem.id}
                    value={menuItem}
                    hideIcons={store.hideMenuItemIcons}
                    expandOnMouseOver={store.expandMenuOnMouseOver}
                    treeMode={store.treeMode}
                    glob={props.glob}
                    getItems={handleGetSectionContent}
                    onClick={getMenuItemClickHanlder(menuItem.type)}
                    onShowActions={handleOnShowActions}
                    onHideActions={handleOnHideActions}
                />
            );
        });
    } else {
        className = 'sidebar-menu';

        _.each(store.menu, (menuItem) => {
            content.push(
                <SidebarMenuItem
                    key={menuItem.type + '_' + menuItem.id}
                    value={menuItem}
                    hideIcons={store.hideMenuItemIcons}
                    glob={props.glob}
                    getItems={handleGetSectionContent}
                    onClick={getMenuItemClickHanlder(menuItem.type)}
                />
            );
        });
    }

    return (
        <div className={className} ref={contentRef}>
            {content}
        </div>
    );
}
